/**
 * P3 (detail) — Block detail page, inverters (UX-002 §06).
 * KPI tiles with trend disclosure, equipment tabs (inverters / transformer /
 * HT panel / LT panel / combiners / alerts / findings), inverter table with
 * band chart + parameter selector, string strip, finding pinned to chart.
 */
import { useMemo, useState, type ReactNode } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { PageHeader } from "../shell/PageHeader";
import {
  useGetV1BlocksId,
  useGetV1Alerts,
  useGetV1Series,
  type GetV1BlocksIdParams,
  type EnvelopeBlockDetailDataDevicesItem,
  type EnvelopeBlockDetailDataSeriesRef,
  type InterventionSummaryItem,
  type Figure,
} from "@solarkey/api-client";
import { BandOutlierChart, TimeSeriesTile } from "@solarkey/charts";
import {
  EmptyState,
  ErrorState,
  HealthBadge,
  HealthDot,
  Metric,
  Skeleton,
  StateWord,
  Term,
  reasonText,
  type HealthState,
} from "@solarkey/ui";
import { useCanAction, useEntitlement } from "../session/capabilities";
import { TrendTileRow, type TrendTileDef } from "../components/TrendTileRow";

type Period = GetV1BlocksIdParams["period"];

const HEALTH_PRIORITY: Record<string, number> = {
  fault: 0, warning: 1, offline: 2, partial: 3, ok: 4, unmonitored: 5,
};

const HT_CLASSES = new Set(["breaker", "isolator", "ct", "pt", "meter", "ht_panel", "panel"]);
const LT_CLASSES = new Set(["lt_panel", "lt_breaker", "mccb", "acdb"]);

type Tab = "inverters" | "transformer" | "ht_panel" | "lt_panel" | "combiners" | "alerts" | "findings";

/* ── Reading the panel's figures ──────────────────────────────────────────────
   Every number on this screen is a Figure the server built, with the basis and
   the state it really has. Nothing here derives a KPI (CLAUDE.md rule 2), and a
   field the panel does not send is WITHHELD with the reason that says why —
   never 0, never a bare dash, and never a basis chip the figure did not carry.
   ──────────────────────────────────────────────────────────────────────────── */

/** A Figure carried on an additive field the generated client does not type yet. */
function extFigure(src: unknown, key: string): Figure | undefined {
  const v = (src as Record<string, unknown> | undefined)?.[key];
  if (!v || typeof v !== "object") return undefined;
  const f = v as Partial<Figure>;
  return typeof f.unit === "string" && typeof f.state === "string" ? (f as Figure) : undefined;
}

/** A figure, or — when the panel sent none at all — the state word that says so.
 *  No unit is invented for a figure that does not exist. */
function FigureOrWithheld(props: {
  figure?: Figure;
  template: string;
  state?: Figure["state"];
  coverage?: boolean;
  digits?: number;
}): ReactNode {
  if (!props.figure) {
    return (
      <StateWord
        state={props.state ?? "CANNOT_CHECK"}
        reason={{ template: props.template, params: {} }}
      />
    );
  }
  return (
    <Metric
      figure={props.figure}
      showFreshness={false}
      showProvenance={false}
      showCoverage={props.coverage}
      maximumFractionDigits={props.digits}
    />
  );
}

/** A nameplate / detail field that is not on the block contract: withheld, not "—". */
function NotOnContract({ template }: { template: string }): ReactNode {
  return <StateWord state="NOT_MEASURED" reason={{ template, params: {} }} />;
}

const OFF_CONTRACT = "NOT_MEASURED_FIELD_NOT_ON_BLOCK_CONTRACT";

/** The reason sentence of a withheld figure, for a screen-state message. */
function reasonSentence(f?: Figure): string | undefined {
  return reasonText(f?.reason) ?? undefined;
}

export function BlockDetail({ site, blockId, period }: {
  site: string;
  blockId: string;
  period?: Period;
}): ReactNode {
  const canRaiseWo = useCanAction("raise_work_order");
  const hasExpectedVsActual = useEntitlement("expected_vs_actual");
  const [activeTab, setActiveTab] = useState<Tab>("inverters");

  // Equipment opens on its own P7 page (…/plants/$site/equipment?equipment=ID),
  // never inline — the block view no longer duplicates the equipment drill-down.
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const scopeMatch = pathname.match(/^\/t\/([^/]+)\/s\/([^/]+)/);
  const openEquipment = (id: string) => {
    const tenant = scopeMatch?.[1] ?? "";
    const scope = scopeMatch?.[2] ?? "";
    void navigate({
      to: `/t/${tenant}/s/${scope}/plants/${site}/equipment` as never,
      search: { equipment: id } as never,
    });
  };

  const q = useGetV1BlocksId(blockId, { period });

  // Every hook must run BEFORE any early return (React rules of hooks). These two
  // used to sit below the loading/error returns, so the success render executed
  // more hooks than the loading render and threw #310 the moment a block loaded.
  const alertsQuery = useGetV1Alerts({ site });
  // /v1/alerts keys each row by the TELEMETRY id the event store files it under,
  // while this panel's device_id is the RDS device_key. Matching on device_id
  // alone never matched, so every block reported "0 alerts · all clear" for a
  // figure that had not been computed. Both keys are matched (the panel now
  // sends telemetry_key alongside device_id).
  const deviceIds = useMemo(() => {
    const ds = q.data?.status === 200 ? (q.data.data.data?.devices ?? []) : [];
    const keys = new Set<string>();
    for (const d of ds) {
      if (d.device_id) keys.add(d.device_id);
      const tk = (d as Record<string, unknown>)["telemetry_key"];
      if (typeof tk === "string" && tk) keys.add(tk);
    }
    return keys;
  }, [q.data]);

  if (q.isLoading) {
    return (
      <div className="content" aria-busy="true">
        <Skeleton variant="text" width="12rem" />
        <Skeleton variant="row" lines={6} />
      </div>
    );
  }

  if (q.isError || !q.data || q.data.status !== 200) {
    return (
      <ErrorState
        title="Block unavailable"
        message={`Could not load block ${blockId}.`}
        onRetry={() => void q.refetch()}
      />
    );
  }

  const data = q.data.data.data;
  const devices = data?.devices ?? [];
  const findings = data?.findings ?? [];
  const seriesRef = data?.series_ref;

  const inverters = devices.filter((d) => d.device_class === "inverter");
  const transformers = devices.filter((d) => d.device_class === "transformer");
  const htPanels = devices.filter((d) => HT_CLASSES.has(d.device_class ?? ""));
  const ltPanels = devices.filter((d) => LT_CLASSES.has(d.device_class ?? ""));
  const combiners = devices.filter((d) => d.device_class === "combiner");

  const runningInv = inverters.filter((d) => (d.health?.state ?? "ok") === "ok" || (d.health?.state ?? "") === "partial").length;
  const faultInv = inverters.find((d) => d.health?.state === "fault");
  const blockCapKw = inverters.reduce((s, d) => {
    const cap = d.nameplate?.capacity_ac;
    return s + (cap && typeof cap.value === "number" ? cap.value : 0);
  }, 0);

  // The block's own figures, as the panel computed them. The screen used to sum
  // the inverters here, coercing every WITHHELD inverter figure to 0 and showing
  // the shortfall as a measured total (and, for energy, under a hardcoded METER
  // chip although it was the Σ of inverter registers).
  const blockPower = extFigure(data, "power");
  const blockEnergy = extFigure(data, "delivered_energy");
  const blockYield = extFigure(data, "specific_yield");
  const blockCoverage = extFigure(data, "coverage");
  const findingsCount = extFigure(data, "findings_count");

  const blockLabel = data?.label ?? blockId;
  const blockNpExt = devices[0]?.nameplate as Record<string, unknown> | undefined;
  const icrLabel = typeof blockNpExt?.icr_label === "string" ? blockNpExt.icr_label
    : typeof blockNpExt?.block_id === "string" ? blockNpExt.block_id : "";
  const capMw = blockCapKw > 0 ? `${Math.round(blockCapKw / 1000)} MW` : "";
  const xfName = transformers[0]?.role_name ?? (transformers.length > 0 ? `transformer` : "");
  const htName = htPanels[0]?.role_name ?? (htPanels.length > 0 ? `HT panel ${htPanels.length}` : "");
  const subtitle = [
    icrLabel,
    capMw,
    `${inverters.length} inverters`,
    xfName,
    htName,
    combiners.length > 0 ? `${combiners.length} combiners` : "",
  ].filter(Boolean).join(" · ");

  const allAlerts = alertsQuery.data?.status === 200 ? (alertsQuery.data.data.data?.alerts ?? []) : [];
  const blockAlerts = allAlerts.filter((a) => !!a.device && deviceIds.has(a.device));

  const partialDevices = inverters.filter((d) => d.health?.state === "partial");

  const powerPct = typeof blockPower?.value === "number" && blockCapKw > 0
    ? Math.round((blockPower.value / blockCapKw) * 100)
    : null;

  const kpiTiles: TrendTileDef[] = [
    {
      key: "power",
      label: "Active power",
      // Metric renders the state word + reason when the figure is withheld, and
      // the coverage bar when the sum stands on only part of the block.
      value: <FigureOrWithheld figure={blockPower} template="CANNOT_CHECK_BLOCK_POWER_NOT_ON_PANEL" coverage digits={1} />,
      sub: powerPct != null ? `${powerPct}% of block AC nameplate` : undefined,
      // No trend disclosure here: TrendSeriesRef carries no `block`, so the
      // panel would draw the whole SITE's curve under a block heading. The
      // block's own curve is the chart in the inverter tab, which is scoped.
    },
    {
      key: "energy",
      label: "Energy today",
      // The basis chip comes from the figure (Metric prints figure.basis), so it
      // can only ever say what the figure was actually measured on.
      value: <FigureOrWithheld figure={blockEnergy} template="CANNOT_CHECK_BLOCK_ENERGY_NOT_ON_PANEL" digits={1} />,
      sub: typeof blockEnergy?.value === "number" ? "block export meters" : undefined,
    },
    {
      key: "yield",
      // UX-REV-001 U3: "specific yield" is specialist vocabulary — the label carries
      // a glossary tooltip (Term). labelText keeps a plain string for the trend
      // panel/chart (uPlot + aria need a string), per TrendTileDef.
      label: <Term tkey="specific_yield">Specific yield</Term>,
      labelText: "Specific yield",
      // Computed on the server (blocks.py), not in the browser.
      value: <FigureOrWithheld figure={blockYield} template="CANNOT_CHECK_SPECIFIC_YIELD_NOT_ON_PANEL" />,
      sub: undefined,
    },
    {
      key: "coverage",
      label: "Coverage",
      value: <FigureOrWithheld figure={blockCoverage} template="NOT_MEASURED_SIGNAL_COVERAGE_NOT_COMPUTED" state="NOT_MEASURED" />,
      sub: partialDevices.length > 0
        ? `${partialDevices[0]?.role_name ?? "device"} reporting partially`
        : undefined,
    },
    {
      key: "inverters",
      label: "Inverters",
      value: <span className="sk-metric__value" style={{ fontVariantNumeric: "tabular-nums" }}>
        {runningInv} of {inverters.length} <span className="sk-metric__unit">running</span>
      </span>,
      sub: faultInv
        ? <><HealthDot state="fault" /> {faultInv.role_name ?? faultInv.device_id} stopped</>
        : undefined,
    },
    {
      key: "open",
      label: "Open",
      // Alerts are a real count from /v1/alerts. Findings are NOT computed for a
      // block, so the count is withheld beside them instead of printing 0 and
      // calling the block "all clear".
      value: <span className="sk-metric__value">
        {alertsQuery.isError
          ? <StateWord state="CANNOT_CHECK" reason={{ template: "CANNOT_CHECK_ALERTS_UNAVAILABLE", params: {} }} />
          : <>{blockAlerts.length} alert{blockAlerts.length !== 1 ? "s" : ""}</>}
      </span>,
      sub: findings.length > 0 && findings[0]
        ? `${findings[0].class ?? "Finding"} · ${findings[0].lane ?? ""}`
        : <>findings: <StateWord
            state={findingsCount?.state ?? "NOT_MEASURED"}
            reason={findingsCount?.reason ?? { template: "NOT_MEASURED_NO_FINDINGS_STORE", params: {} }}
          /></>,
    },
  ];

  const tabDefs: ({ key: Tab; label: string; count: number; showCount: boolean; dot?: boolean } | null)[] = [
    inverters.length > 0 ? { key: "inverters", label: "Inverters", count: inverters.length, showCount: true } : null,
    transformers.length > 0 ? { key: "transformer", label: transformers[0]?.role_name ?? "Transformer", count: transformers.length, showCount: false } : null,
    htPanels.length > 0 ? { key: "ht_panel", label: htPanels[0]?.role_name ?? `HT panel ${htPanels.length}`, count: htPanels.length, showCount: false } : null,
    ltPanels.length > 0 ? { key: "lt_panel", label: ltPanels[0]?.role_name ?? "LT panel", count: ltPanels.length, showCount: false } : null,
    combiners.length > 0 ? { key: "combiners", label: "Combiners", count: combiners.length, showCount: true } : null,
    { key: "alerts", label: "Alerts", count: blockAlerts.length, showCount: true },
    { key: "findings", label: "Findings", count: findings.length, showCount: true, dot: findings.length > 0 },
  ];
  const tabs = tabDefs.filter((t): t is NonNullable<typeof t> => t != null);

  return (
    <div className="content">
      <PageHeader
        title={blockLabel}
        scope={subtitle}
        action={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {canRaiseWo && (
              <button type="button" className="sk-btn sk-btn--primary" disabled style={{ opacity: 0.6 }}>
                Raise work order
              </button>
            )}
          </div>
        }
      />

      {/* ── KPI tiles with trend disclosure (annotation 1) ── */}
      <TrendTileRow tiles={kpiTiles} columns={`repeat(${kpiTiles.length}, minmax(0, 1fr))`} />
      {/* QA#1 / UX-REV-001 ENERGY-TODAY: these headline tiles are a today/now snapshot —
          the blocks endpoint serves today only (blocks.py), so the value does NOT follow
          the period navigator in the header. Keeping the label "Energy today" would read
          ambiguously next to that navigator, so the band states the snapshot plainly
          rather than relabelling (which would make the figure lie). The snapshot-vs-
          period-aware-backend question is tracked for Vikram as C4. */}
      <div className="text-text-muted" style={{ fontSize: 11, marginTop: 4 }}>
        Live snapshot — today&rsquo;s energy and current power, not the period selected above.
      </div>

      {/* ── Equipment tabs (annotation 2) ── */}
      <div className="sk-block-tabs" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={t.key === activeTab}
            className={`sk-block-tab${t.key === activeTab ? " sk-block-tab--on" : ""}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}{t.showCount ? ` (${t.count})` : ""}
            {t.dot && <span className="sk-block-tab__dot" />}
          </button>
        ))}
      </div>

      {/* ── Tab content ── */}
      {activeTab === "inverters" ? (
        <InverterTab
          devices={inverters}
          seriesRef={seriesRef}
          showBand={hasExpectedVsActual}
          findings={findings}
          canRaiseWo={canRaiseWo}
          onOpenEquipment={openEquipment}
          site={site}
        />
      ) : activeTab === "transformer" ? (
        <TransformerTab
          transformer={transformers[0] ?? null}
          htPanels={htPanels}
          ltPanels={ltPanels}
          blockAlerts={blockAlerts}
          site={site}
          onOpenEquipment={openEquipment}
        />
      ) : activeTab === "ht_panel" ? (
        <PanelTab devices={htPanels} kind="ht" onOpenEquipment={openEquipment} />
      ) : activeTab === "lt_panel" ? (
        <PanelTab devices={ltPanels} kind="lt" onOpenEquipment={openEquipment} />
      ) : activeTab === "combiners" ? (
        <CombinersTab devices={combiners} onOpenEquipment={openEquipment} />
      ) : activeTab === "alerts" ? (
        <AlertsTab alerts={blockAlerts} loading={alertsQuery.isLoading} error={alertsQuery.isError} />
      ) : (
        <FindingsTab findings={findings} canRaiseWo={canRaiseWo} countFigure={findingsCount} />
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Inverter tab: device table + band chart + parameter selector + string strip
   (wireframe 06, annotations 4–6)
   ════════════════════════════════════════════════════════════════════════════ */

function InverterTab({ devices, seriesRef, showBand, findings, canRaiseWo, onOpenEquipment, site }: {
  devices: EnvelopeBlockDetailDataDevicesItem[];
  seriesRef?: EnvelopeBlockDetailDataSeriesRef;
  showBand?: boolean;
  findings: InterventionSummaryItem[];
  canRaiseWo?: boolean;
  onOpenEquipment: (id: string) => void;
  site: string;
}): ReactNode {
  const [selectedInv, setSelectedInv] = useState<string | null>(null);

  const activeInvId = selectedInv ?? devices[0]?.device_id ?? null;
  const activeInv = devices.find((d) => d.device_id === activeInvId);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 16 }}>
        {/* Left: inverter table */}
        <DeviceTable
          devices={devices}
          label="Inverter"
          collapse
          onOpen={onOpenEquipment}
          selectedId={activeInvId}
          onSelect={setSelectedInv}
        />

        {/* Right: the block's AC-power chart (annotation 4).
            The parameter select, the Band / All-lines switch and the Explorer
            button were wired to nothing: /v1/series takes one signal and returns
            no lo/hi, so the "band" could never be drawn and the other parameters
            could not be fetched. They are gone rather than shown as working, and
            with them the caption that described a band nobody draws. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600, fontSize: 12 }}>AC power, all inverters in this block</span>
            <span className="text-text-muted" style={{ fontSize: 11 }}>today · as reported</span>
          </div>

          <SeriesChart seriesRef={seriesRef} showBand={showBand !== false} />

          {/* Finding pinned to chart (annotation 5) */}
          {findings.length > 0 && findings[0] ? (
            <FindingPinned finding={findings[0]} canRaiseWo={canRaiseWo} />
          ) : null}
        </div>
      </div>

      {/* ── String strip (annotation 6) ── */}
      {activeInv && (
        <StringStrip device={activeInv} site={site} />
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Transformer tab (wireframe 07) — two-column layout:
   left: transformer nameplate + 6 KPI tiles + dual chart
   right: HT panel card + LT panel card
   ════════════════════════════════════════════════════════════════════════════ */

function TransformerTab({ transformer, htPanels, ltPanels, blockAlerts, site, onOpenEquipment }: {
  transformer: EnvelopeBlockDetailDataDevicesItem | null;
  htPanels: EnvelopeBlockDetailDataDevicesItem[];
  ltPanels: EnvelopeBlockDetailDataDevicesItem[];
  blockAlerts: Array<Record<string, unknown>>;
  site: string;
  onOpenEquipment: (id: string) => void;
}): ReactNode {
  const [chartParam, setChartParam] = useState<"loading" | "oti">("loading");
  // "now" once per mount — a fresh timestamp each render loops the fetch forever.
  // Declared BEFORE the early return so the hook order is stable across renders
  // (react-hooks/rules-of-hooks: a useMemo after a conditional return is a bug).
  const nowIso = useMemo(() => new Date().toISOString(), []);

  if (!transformer) {
    return <EmptyState variant="no_data" title="No transformer in this block" />;
  }

  const np = transformer.nameplate;
  const ext = np as Record<string, unknown> | undefined;
  const label = transformer.role_name ?? transformer.device_id ?? "Transformer";
  const health = (transformer.health?.state ?? "unmonitored") as HealthState;
  const isPartial = health === "partial";

  const ratedKva = typeof np?.rated_kva === "number" ? np.rated_kva : null;
  const ratedMva = ratedKva ? `${Math.round(ratedKva / 1000)} MVA` : "";
  const ratedVoltage = typeof ext?.rated_voltage === "string" ? ext.rated_voltage : "";
  const vectorGroup = typeof ext?.vector_group === "string" ? ext.vector_group : "";
  const coolingType = typeof ext?.cooling_type === "string" ? ext.cooling_type : "";
  const specParts = [ratedMva, ratedVoltage, vectorGroup, coolingType].filter(Boolean);
  const specLine = specParts.join(" · ");

  // "No winding temperature / no tap position" is stated ONLY when the registry
  // says so (an explicit false). An ABSENT field used to produce the same claim,
  // so every transformer was announced as missing both sensors whether it was or
  // not — absence of a record is not a record of absence.
  const missingSensors: string[] = [];
  if (ext?.has_winding_temp === false) missingSensors.push("winding temperature");
  if (ext?.has_tap_position === false) missingSensors.push("tap position");

  const oilType = typeof ext?.oil_type === "string" ? ext.oil_type : "";
  const oilKg = typeof ext?.oil_kg === "number" ? ext.oil_kg : null;
  const behindMeter = typeof ext?.behind_meter === "string" ? ext.behind_meter : "";
  const protection = typeof ext?.protection === "string" ? ext.protection : "";
  const warrantyTo = typeof ext?.warranty_to === "string" ? ext.warranty_to : null;

  const xfKeys = new Set(
    [transformer.device_id, (transformer as Record<string, unknown>)["telemetry_key"]]
      .filter((v): v is string => typeof v === "string" && !!v),
  );
  const xfAlerts = blockAlerts.filter((a) => typeof a.device === "string" && xfKeys.has(a.device));

  const loadingPct = typeof transformer.power?.value === "number" && ratedKva
    ? Math.round((transformer.power.value / ratedKva) * 100)
    : null;
  const loadingMva = typeof transformer.power?.value === "number"
    ? (transformer.power.value / 1000).toFixed(1)
    : null;

  const oilTemp = typeof ext?.oil_temperature === "number" ? ext.oil_temperature : null;
  // No default thresholds. 75 / 85 °C were invented here and then labelled
  // "threshold: site", which presented two made-up numbers as this plant's
  // protection settings (CLAUDE.md rule 6: fail loudly, never silently default).
  const oilThreshold = typeof ext?.oil_threshold === "number" ? ext.oil_threshold : null;
  const oilTrip = typeof ext?.oil_trip === "number" ? ext.oil_trip : null;

  const windingTemp = typeof ext?.winding_temperature === "number" ? ext.winding_temperature : null;

  const lvVoltage = typeof ext?.lv_voltage === "number" ? ext.lv_voltage : null;
  const lvVoltageRY = typeof ext?.lv_voltage_ry === "number" ? ext.lv_voltage_ry : null;
  const lvVoltageYB = typeof ext?.lv_voltage_yb === "number" ? ext.lv_voltage_yb : null;
  const lvVoltageBR = typeof ext?.lv_voltage_br === "number" ? ext.lv_voltage_br : null;

  const tapPos = typeof ext?.tap_position === "number" ? ext.tap_position : null;
  const tapSet = typeof ext?.tap_set_on_record === "number" ? ext.tap_set_on_record : null;

  const todayIso = new Date(new Date().setHours(0, 0, 0, 0)).toISOString();

  const xfTiles: TrendTileDef[] = [
    {
      key: "loading",
      label: "Loading",
      value: loadingPct != null
        ? <span className="sk-metric__value">{loadingPct} <span className="sk-metric__unit">%</span></span>
        : <StateWord state="NOT_MEASURED" />,
      sub: loadingMva && ratedMva
        ? `${loadingMva} MVA of ${ratedMva.replace(" MVA", "")} · reported`
        : "reported",
      series: { site, signal: "transformer_loading", from: todayIso, to: nowIso, unit: "%", label: "today" },
    },
    {
      key: "oil_temp",
      label: "Oil temperature · OTI",
      value: oilTemp != null
        ? <span className="sk-metric__value">{oilTemp} <span className="sk-metric__unit">°C</span></span>
        : <StateWord state="NOT_MEASURED" />,
      // The threshold chip appears only when the thresholds really are on record.
      sub: oilThreshold != null
        ? <><span>{`threshold ${oilThreshold}${oilTrip != null ? ` · trip ${oilTrip}` : ""}`}</span><br /><span className="sk-chip sk-chip--basis">threshold: site</span></>
        : <>alarm thresholds: <NotOnContract template="NOT_MEASURED_PROTECTION_SETTINGS_NOT_ON_RECORD" /></>,
      series: { site, signal: "transformer_oti", from: todayIso, to: nowIso, unit: "°C", label: "today" },
    },
    {
      key: "winding",
      label: "Winding · WTI",
      value: windingTemp != null
        ? <span className="sk-metric__value">{windingTemp} <span className="sk-metric__unit">°C</span></span>
        : <StateWord state="NOT_MEASURED" />,
      sub: windingTemp == null ? <><span className="sk-xf-annot">no sensor mapped</span></> : undefined,
    },
    {
      key: "lv_voltage",
      label: "LV line voltage",
      value: lvVoltage != null
        ? <span className="sk-metric__value">{lvVoltage} <span className="sk-metric__unit">V</span></span>
        : <StateWord state="NOT_MEASURED" />,
      sub: lvVoltageRY != null
        ? `R-Y ${lvVoltageRY} · Y-B ${lvVoltageYB ?? "—"} · B-R ${lvVoltageBR ?? "—"}`
        : undefined,
    },
    {
      key: "tap_position",
      label: "Tap position",
      value: tapPos != null
        ? <span className="sk-metric__value">{tapPos}</span>
        : <StateWord state="NOT_MEASURED" />,
      sub: tapPos == null && tapSet != null
        ? `manual tap · set ${tapSet} on record`
        : tapPos == null ? "manual tap" : undefined,
    },
    {
      key: "xf_alerts",
      label: "Alerts",
      value: <span className="sk-metric__value">{xfAlerts.length} <span className="sk-metric__unit">open</span></span>,
      sub: xfAlerts.length > 0 && xfAlerts[0]
        ? `${String(xfAlerts[0].code ?? "")} ${String(xfAlerts[0].event_type ?? "")}`.trim()
        : "all clear",
    },
  ];

  return (
    <div className="sk-xf-grid">
      {/* ── Left: transformer detail ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Header + specs */}
        <div className="sk-xf-header">
          <h3 className="sk-xf-title">
            {label}
            {specLine && <span className="sk-xf-spec">{specLine}</span>}
          </h3>
          {isPartial && missingSensors.length > 0 && (
            <span className="sk-xf-partial">
              partially monitored · no {missingSensors.join(", ")}
            </span>
          )}
        </div>

        {/* Nameplate grid (annotation: 4 columns) */}
        {/* Only make / model / serial / commissioned are on the block contract.
            Warranty, rated voltage, oil, behind-meter and protection are not —
            they used to render "—" on every site forever, which reads as "no
            value" rather than "this field never reaches this screen". Each is
            withheld with that as its reason until the registry export carries
            it (see the registry-export gap list). */}
        {np && (
          <div className="sk-xf-nameplate">
            <NpRow
              label="Make and model"
              value={[np.make, np.model].filter(Boolean).join(" · ")
                || <NotOnContract template="NOT_MEASURED_NAMEPLATE_NOT_ON_RECORD" />}
            />
            <NpRow
              label="Serial"
              value={np.serial ?? <NotOnContract template="NOT_MEASURED_NAMEPLATE_NOT_ON_RECORD" />}
              mono
            />
            <NpRow
              label="Commissioned"
              value={formatDate(np.effective_from ?? undefined)
                ?? <NotOnContract template="NOT_MEASURED_COMMISSIONING_DATE_NOT_ON_RECORD" />}
            />
            <NpRow
              label="Warranty to"
              value={warrantyTo
                ? formatDate(warrantyTo) ?? warrantyTo
                : <NotOnContract template={OFF_CONTRACT} />}
            />
            <NpRow
              label="Rated voltage"
              value={ratedVoltage || <NotOnContract template={OFF_CONTRACT} />}
            />
            <NpRow
              label="Oil"
              value={[oilKg ? `${oilKg.toLocaleString()} kg` : null, oilType].filter(Boolean).join(" · ")
                || <NotOnContract template={OFF_CONTRACT} />}
            />
            <NpRow
              label="Behind meter"
              value={behindMeter || <NotOnContract template={OFF_CONTRACT} />}
            />
            <NpRow
              label="Protection"
              value={protection || <NotOnContract template={OFF_CONTRACT} />}
            />
          </div>
        )}

        {/* 6 KPI tiles */}
        <TrendTileRow tiles={xfTiles} columns="repeat(6, minmax(0, 1fr))" />

        {/* Dual chart: loading + oil temperature (annotation 3) */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600, fontSize: 12 }}>Loading and oil temperature</span>
            <span className="text-text-muted" style={{ fontSize: 11 }}>today · two units, two tiles, one x axis</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div className="sk-chart-mode" role="group" aria-label="Chart parameter">
              <button
                type="button"
                className={`sk-chart-mode__btn${chartParam === "loading" ? " sk-chart-mode__btn--on" : ""}`}
                onClick={() => setChartParam("loading")}
              >
                Loading
              </button>
              <button
                type="button"
                className={`sk-chart-mode__btn${chartParam === "oti" ? " sk-chart-mode__btn--on" : ""}`}
                onClick={() => setChartParam("oti")}
              >
                OTI
              </button>
            </div>
            {/* No Explorer button: there is no explorer route wired to it. */}
          </div>

          <DualChart site={site} param={chartParam} />
        </div>
      </div>

      {/* ── Right: HT panel + LT panel cards ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {htPanels.length > 0 && <HTPanelCard device={htPanels[0]!} onOpen={onOpenEquipment} />}
        {ltPanels.length > 0 && <LTPanelCard device={ltPanels[0]!} onOpen={onOpenEquipment} />}
        {htPanels.length === 0 && ltPanels.length === 0 && (
          <EmptyState variant="no_data" title="No panel devices" />
        )}
      </div>
    </div>
  );
}

function NpRow({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }): ReactNode {
  return (
    <>
      <span className="sk-xf-np__label">{label}</span>
      <span className={mono ? "sk-xf-np__val code" : "sk-xf-np__val"}>{value}</span>
    </>
  );
}

/* ── HT panel card (annotation 4) ──
   Only the device's identity, health and `power` Figure are on the block
   contract. Line voltage, per-phase voltage/current, imbalance, breaker state,
   CT ratio and last trip are NOT: they were read off the device object, were
   never present, and rendered "—" on every site forever — under a hardcoded
   "measured · panel meter" provenance line in the case of active power. Each is
   now withheld with the reason that says the field does not reach this screen. */
function HTPanelCard({ device, onOpen }: {
  device: EnvelopeBlockDetailDataDevicesItem;
  onOpen: (id: string) => void;
}): ReactNode {
  const np = device.nameplate as Record<string, unknown> | undefined;
  const label = device.role_name ?? device.device_id ?? "HT panel";
  const health = (device.health?.state ?? "unmonitored") as HealthState;

  const htVoltage = typeof np?.["ht_voltage_kv"] === "number" ? np["ht_voltage_kv"] : null;
  const feeders = typeof np?.["feeder_count"] === "number" ? np["feeder_count"] : null;
  const icogType = typeof np?.["icog_type"] === "string" ? np["icog_type"] : "";
  const specParts = [
    htVoltage != null ? `${htVoltage} kV` : null,
    icogType || null,
    feeders != null ? `${feeders} feeder${feeders !== 1 ? "s" : ""}` : null,
  ].filter(Boolean);

  return (
    <div className="sk-panel-card">
      <div className="sk-panel-card__head">
        <h4>{label}</h4>
        {specParts.length > 0 && <span className="sk-panel-card__spec">{specParts.join(" · ")}</span>}
        <span style={{ marginLeft: "auto" }}><HealthDot state={health} /></span>
        <span className="sk-panel-card__state">{health}</span>
      </div>

      <div className="sk-panel-card__tiles">
        <div className="sk-panel-tile">
          <span className="sk-panel-tile__label">Active power</span>
          <span className="sk-panel-tile__value">
            {device.power
              ? <Metric figure={device.power} showFreshness={false} showBasis={false} showProvenance maximumFractionDigits={1} />
              : <NotOnContract template={OFF_CONTRACT} />}
          </span>
        </div>
        <div className="sk-panel-tile">
          <span className="sk-panel-tile__label">Line voltage</span>
          <span className="sk-panel-tile__value">
            <NotOnContract template={OFF_CONTRACT} />
          </span>
        </div>
        <div className="sk-panel-tile">
          <span className="sk-panel-tile__label">Current</span>
          <span className="sk-panel-tile__value">
            <NotOnContract template={OFF_CONTRACT} />
          </span>
          <span className="sk-panel-tile__sub">per-phase current and imbalance need the panel signals</span>
        </div>
      </div>

      {device.device_id && (
        <button
          type="button"
          className="btn small"
          style={{ marginTop: 8 }}
          onClick={() => device.device_id && onOpen(device.device_id)}
        >
          Full equipment detail
        </button>
      )}
    </div>
  );
}

/* ── LT panel card (annotation 5) ──
   Same rule as the HT card: frequency, power factor (which used to print
   "lagging" from `pf_lagging ?? true` for a figure that was absent) and the
   auxiliary draw are not on the block contract, so they are withheld. */
function LTPanelCard({ device, onOpen }: {
  device: EnvelopeBlockDetailDataDevicesItem;
  onOpen: (id: string) => void;
}): ReactNode {
  const np = device.nameplate as Record<string, unknown> | undefined;
  const label = device.role_name ?? device.device_id ?? "LT panel";
  const health = (device.health?.state ?? "unmonitored") as HealthState;

  const inCount = typeof np?.["in_count"] === "number" ? np["in_count"] : null;
  const outCount = typeof np?.["out_count"] === "number" ? np["out_count"] : null;
  const busVoltage = typeof np?.["bus_voltage_v"] === "number" ? np["bus_voltage_v"] : null;
  const busCurrentA = typeof np?.["bus_current_a"] === "number" ? np["bus_current_a"] : null;
  const devClass = typeof np?.["sub_class"] === "string" ? np["sub_class"] : (device.device_class ?? "");
  const specParts = [
    devClass ? devClass.toUpperCase() : null,
    inCount != null ? `${inCount} in` : null,
    outCount != null ? `${outCount} out` : null,
    busVoltage != null ? `${busVoltage} V` : null,
    busCurrentA != null ? `${busCurrentA.toLocaleString()} A` : null,
  ].filter(Boolean);

  return (
    <div className="sk-panel-card">
      <div className="sk-panel-card__head">
        <h4>{label}</h4>
        {specParts.length > 0 && <span className="sk-panel-card__spec">{specParts.join(" · ")}</span>}
        <span style={{ marginLeft: "auto" }}><HealthDot state={health} /></span>
        <span className="sk-panel-card__state">{health}</span>
      </div>

      <div className="sk-panel-card__tiles">
        <div className="sk-panel-tile">
          <span className="sk-panel-tile__label">Frequency</span>
          <span className="sk-panel-tile__value"><NotOnContract template={OFF_CONTRACT} /></span>
        </div>
        <div className="sk-panel-tile">
          <span className="sk-panel-tile__label">Power factor</span>
          <span className="sk-panel-tile__value"><NotOnContract template={OFF_CONTRACT} /></span>
        </div>
        <div className="sk-panel-tile">
          <span className="sk-panel-tile__label">Auxiliary draw</span>
          <span className="sk-panel-tile__value"><NotOnContract template={OFF_CONTRACT} /></span>
        </div>
      </div>

      {device.device_id && (
        <button
          type="button"
          className="btn small"
          style={{ marginTop: 8 }}
          onClick={() => device.device_id && onOpen(device.device_id)}
        >
          Full equipment detail
        </button>
      )}
    </div>
  );
}



/* ── Dual chart for transformer (loading + OTI, shared x-axis, annotation 3) ── */
function DualChart({ site, param }: { site: string; param: "loading" | "oti" }): ReactNode {
  const from = new Date(new Date().setHours(0, 0, 0, 0)).toISOString();
  // "now" once per mount — a fresh timestamp each render loops the fetch forever.
  const to = useMemo(() => new Date().toISOString(), []);

  const loadingQ = useGetV1Series(
    { site, signal: "transformer_loading", from, to },
    { query: { enabled: true } },
  );
  const otiQ = useGetV1Series(
    { site, signal: "transformer_oti", from, to },
    { query: { enabled: true } },
  );

  const loading = loadingQ.data?.status === 200 ? loadingQ.data.data.data : undefined;
  const oti = otiQ.data?.status === 200 ? otiQ.data.data.data : undefined;

  const loadPts = loading?.points ?? [];
  const otiPts = oti?.points ?? [];

  const isLoading = loadingQ.isLoading || otiQ.isLoading;
  if (isLoading) {
    return <div style={{ height: 240, display: "grid", placeItems: "center", color: "var(--sk-color-ink-3)", fontSize: 11 }}>Loading…</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {(param === "loading" || param === "oti") && loadPts.length > 0 ? (
        <TimeSeriesTile
          label="Loading %"
          unit="%"
          times={loadPts.map((p) => Date.parse(p.ts) / 1000)}
          values={loadPts.map((p) => p.value ?? null)}
          height={140}
        />
      ) : (
        <div style={{ height: 140, display: "grid", placeItems: "center", color: "var(--sk-color-ink-3)", fontSize: 11 }}>No loading series</div>
      )}
      {otiPts.length > 0 ? (
        <TimeSeriesTile
          label="Oil temperature · OTI °C"
          unit="°C"
          times={otiPts.map((p) => Date.parse(p.ts) / 1000)}
          values={otiPts.map((p) => p.value ?? null)}
          height={140}
        />
      ) : (
        <div style={{ height: 140, display: "grid", placeItems: "center", color: "var(--sk-color-ink-3)", fontSize: 11 }}>No OTI series</div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Panel tab — focused view for HT / LT panel when selected individually
   ════════════════════════════════════════════════════════════════════════════ */

function PanelTab({ devices, kind, onOpenEquipment }: {
  devices: EnvelopeBlockDetailDataDevicesItem[];
  kind: "ht" | "lt";
  onOpenEquipment: (id: string) => void;
}): ReactNode {
  if (devices.length === 0) {
    return <EmptyState variant="no_data" title={`No ${kind.toUpperCase()} panel devices`} />;
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: devices.length > 1 ? "1fr 1fr" : "1fr", gap: 16 }}>
      {devices.map((d, i) =>
        kind === "ht"
          ? <HTPanelCard key={d.device_id ?? i} device={d} onOpen={onOpenEquipment} />
          : <LTPanelCard key={d.device_id ?? i} device={d} onOpen={onOpenEquipment} />,
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Combiners tab — simple device list with health, power, energy
   ════════════════════════════════════════════════════════════════════════════ */

function CombinersTab({ devices, onOpenEquipment }: {
  devices: EnvelopeBlockDetailDataDevicesItem[];
  onOpenEquipment: (id: string) => void;
}): ReactNode {
  if (devices.length === 0) {
    return <EmptyState variant="no_data" title="No combiners in this block" />;
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="table" aria-label="Combiner devices">
        <thead>
          <tr>
            <th>Combiner</th>
            <th>Health</th>
            <th className="num">Power</th>
            <th className="num">Energy today</th>
            <th>Fresh</th>
            <th><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {devices.map((d, i) => (
            <tr key={d.device_id ?? i}>
              <td><strong>{d.role_name ?? d.device_id ?? `Combiner ${i + 1}`}</strong></td>
              <td><HealthBadge state={(d.health?.state ?? "unmonitored") as HealthState} showLabel /></td>
              <td className="num">
                {d.power ? <Metric figure={d.power} showFreshness={false} showBasis={false} showProvenance={false} /> : "—"}
              </td>
              <td className="num">
                {d.energy ? <Metric figure={d.energy} showFreshness={false} showBasis={false} showProvenance={false} /> : "—"}
              </td>
              <td>{d.freshness?.age_s != null ? formatAge(d.freshness.age_s) : "—"}</td>
              <td>
                <button
                  type="button"
                  className="btn small"
                  onClick={() => d.device_id && onOpenEquipment(d.device_id)}
                >
                  Open
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Device table (inverter table, annotation 4)
   ════════════════════════════════════════════════════════════════════════════ */

const INV_CAP = 12;

function DeviceTable({ devices, onOpen, label, collapse, selectedId, onSelect }: {
  devices: EnvelopeBlockDetailDataDevicesItem[];
  onOpen: (id: string) => void;
  label?: string;
  collapse?: boolean;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
}): ReactNode {
  const sorted = useMemo(() => {
    const items = [...devices];
    items.sort((a, b) => {
      const pa = HEALTH_PRIORITY[(a.health?.state ?? "unmonitored")] ?? 5;
      const pb = HEALTH_PRIORITY[(b.health?.state ?? "unmonitored")] ?? 5;
      return pa - pb;
    });
    return items;
  }, [devices]);

  const escapees = sorted.filter(
    (d) => Math.abs(d.deviation?.value ?? 0) > 5 || (d.health?.state ?? "ok") !== "ok",
  );

  const shown = !collapse
    ? sorted
    : escapees.length > 0 ? escapees : sorted.slice(0, INV_CAP);
  const hidden = sorted.length - shown.length;
  const noun = label ?? "Device";
  const [localSel, setLocalSel] = useState<string | null>(null);
  const activeId = selectedId ?? localSel ?? shown[0]?.device_id ?? null;

  const partialDevices = shown.filter((d) => d.health?.state === "partial");

  if (sorted.length === 0) {
    return <EmptyState variant="no_data" title={`No ${noun.toLowerCase()} devices`} />;
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table className="table" aria-label={`${noun} devices`}>
        <thead>
          <tr>
            <th>{noun}</th>
            <th>Health</th>
            <th className="num">Power</th>
            <th className="num">Today</th>
            <th className="num">vs block</th>
            <th>Fresh</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((d, i) => {
            const isSel = !!d.device_id && d.device_id === activeId;
            return (
              <tr
                key={d.device_id ?? i}
                className={isSel ? "sel" : ""}
                onClick={() => {
                  if (d.device_id) {
                    setLocalSel(d.device_id);
                    onSelect?.(d.device_id);
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                <td>
                  {/* The inverter name itself is the click-through to the device's
                      full detail (P7 equipment page) — QA finding #2: a block's
                      inverter had no row-level link, only a generic "Open" button.
                      Row click still selects the inverter for the chart/string strip;
                      the name link stops propagation so the two don't collide. */}
                  {d.device_id ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onOpen(d.device_id!); }}
                      aria-label={`Open ${d.role_name ?? d.device_id} detail`}
                      style={{
                        background: "none", border: "none", padding: 0, font: "inherit",
                        fontWeight: 700, color: "var(--sk-color-accent, #0066cc)",
                        cursor: "pointer", textAlign: "left",
                      }}
                    >
                      {d.role_name ?? d.device_id}
                    </button>
                  ) : (
                    <strong>{d.role_name ?? "—"}</strong>
                  )}
                </td>
                <td><HealthBadge state={(d.health?.state ?? "unmonitored") as HealthState} showLabel /></td>
                <td className="num">
                  {d.power ? <Metric figure={d.power} showFreshness={false} showBasis={false} showProvenance={false} /> : "—"}
                </td>
                <td className="num">
                  {typeof d.energy?.value === "number"
                    ? <Metric figure={d.energy} showFreshness={false} showBasis={false} showProvenance={false} />
                    : <span className="muted">—</span>}
                </td>
                <td className="num">
                  {typeof d.deviation?.value === "number" ? (
                    <span>{d.deviation.value > 0 ? "+" : ""}{d.deviation.value.toFixed(1)}%</span>
                  ) : "—"}
                </td>
                <td>{d.freshness?.age_s != null ? formatAge(d.freshness.age_s) : "—"}</td>
              </tr>
            );
          })}
          {hidden > 0 ? (
            <tr>
              <td colSpan={6} className="more">
                {hidden} more within band
                {/* "no cabinet temperature" was copied from the wireframe and
                    asserted as the reason on every site. The panel sends the
                    partial state, not which signal is missing, so only the
                    state is stated. */}
                {partialDevices.length > 0 && partialDevices[0]
                  ? ` · ${partialDevices[0].role_name ?? partialDevices[0].device_id} reporting partially`
                  : ""}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Series chart (band or simple line)
   ════════════════════════════════════════════════════════════════════════════ */

function SeriesChart({ seriesRef, showBand }: {
  seriesRef?: EnvelopeBlockDetailDataSeriesRef;
  showBand?: boolean;
}): ReactNode {
  const ref = seriesRef;
  const query = useGetV1Series(
    {
      site: ref?.site ?? "", signal: ref?.signal ?? "",
      // Scope the curve to THIS block's inverters. Without `block` the chart
      // drew the whole site's curve under the heading "AC power · Block B".
      ...(ref?.block ? { block: ref.block } : {}),
      from: ref?.from ?? "", to: ref?.to ?? "",
    },
    { query: { enabled: !!(ref?.site && ref?.signal && ref?.from && ref?.to) } },
  );

  if (!ref?.site || !ref?.signal) {
    return <EmptyState variant="no_data" title="No series for this block" />;
  }
  if (query.isLoading) return <Skeleton variant="tile" />;
  if (query.isError || !query.data || query.data.status !== 200) {
    return <EmptyState variant="no_data" title="AC power" message="Series unavailable" />;
  }

  const s = query.data.data.data;
  const points = s.points ?? [];
  if (points.length === 0) return <EmptyState variant="no_data" title="AC power" />;

  const times = points.map((p) => Date.parse(p.ts) / 1000);
  const values = points.map((p) => p.value ?? null);
  // The normalised band (wireframe 06 annotation 5). `/v1/series` serves no `lo`/`hi`,
  // so these read as absent — NOT as 0. Defaulting them to 0 made `hasBand` false for
  // the wrong reason, and would have drawn a band pinned to the floor the day the
  // field arrived carrying a real 0.
  const band = points.map((p) => {
    const raw = p as unknown as { lo?: number | null; hi?: number | null };
    return { lo: typeof raw.lo === "number" ? raw.lo : null,
             hi: typeof raw.hi === "number" ? raw.hi : null };
  });
  const hasBand = band.some((b) => b.lo !== null || b.hi !== null);
  const lo = band.map((b) => b.lo ?? 0);
  const hi = band.map((b) => b.hi ?? 0);

  if (hasBand && showBand) {
    return <BandOutlierChart label="AC power · normalised band" unit={s.unit ?? ""} times={times} values={values} lo={lo} hi={hi} />;
  }
  return <TimeSeriesTile label="AC power" unit={s.unit ?? ""} times={times} values={values} />;
}

/* ════════════════════════════════════════════════════════════════════════════
   String strip (annotation 6) — density-adaptive summary for selected inverter
   ════════════════════════════════════════════════════════════════════════════ */

function StringStrip({ device }: {
  device: EnvelopeBlockDetailDataDevicesItem;
  site: string;
}): ReactNode {
  const np = device.nameplate as Record<string, unknown> | undefined;
  // The registry's real string layout: MPPT count × strings per MPPT (or the
  // panel's own string_count). No per-string health exists: the strip used to
  // mark every string "ok" except the last two, which invented both a healthy
  // majority and two unmonitored strings for every inverter on the estate.
  const direct = typeof np?.["string_count"] === "number" ? np["string_count"] : null;
  const mppt = typeof np?.["mppt_count"] === "number" ? np["mppt_count"] : null;
  const per = typeof np?.["strings_per_mppt"] === "number" ? np["strings_per_mppt"] : null;
  const stringCount = direct ?? (mppt != null && per != null ? mppt * per : null);
  const label = device.role_name ?? device.device_id ?? "Inverter";

  if (stringCount == null && mppt == null) return null;

  return (
    <div className="sk-string-strip">
      <span style={{ fontWeight: 600, fontSize: 12 }}>Strings</span>
      <span className="text-text-muted" style={{ fontSize: 12 }}>
        {label}
        {mppt != null ? ` · ${mppt} MPPTs` : ""}
        {stringCount != null ? ` · ${stringCount} strings on record` : ""}
        {" · per-string current "}
        <StateWord
          state="NOT_MEASURED"
          reason={{ template: "NOT_MEASURED_NO_STRING_SERIES_ENDPOINT", params: {} }}
        />
      </span>
      {/* No "Open strings" button: it was wired to nothing. The strings screen
          is reached from the site tab bar. */}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Finding pinned callout (annotation 5)
   ════════════════════════════════════════════════════════════════════════════ */

function FindingPinned({ finding, canRaiseWo }: {
  finding: InterventionSummaryItem;
  canRaiseWo?: boolean;
}): ReactNode {
  return (
    <div className="sk-finding-pinned">
      <span>
        finding pinned: <strong>{finding.class ?? "Finding"}</strong>
        {finding.lane ? ` · ${finding.lane}` : ""}
      </span>
      {canRaiseWo ? (
        <button type="button" className="sk-btn sk-btn--primary" style={{ marginLeft: "auto", padding: "3px 10px", fontSize: 11 }} disabled>
          Raise work order
        </button>
      ) : null}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Alerts tab (annotation 2)
   ════════════════════════════════════════════════════════════════════════════ */

function AlertsTab({ alerts, loading, error }: {
  alerts: Array<Record<string, unknown>>;
  loading: boolean;
  error: boolean;
}): ReactNode {
  if (loading) return <Skeleton variant="row" lines={4} />;
  if (error) return <EmptyState variant="no_data" title="Alerts unavailable" />;
  if (alerts.length === 0) return <EmptyState variant="verified_quiet" title="No active alerts for this block" />;

  return (
    <table className="table" aria-label="Alerts">
      <thead>
        <tr>
          <th>Severity</th>
          <th>Device</th>
          <th>Type</th>
          <th>Code</th>
          <th>Count</th>
          <th>Last seen</th>
          <th>Active</th>
        </tr>
      </thead>
      <tbody>
        {alerts.map((a, i) => (
          <tr key={`${String(a.device ?? "")}-${String(a.code ?? "")}-${i}`}>
            <td>
              {/* The alerts panel sends severity_label; inventing one from the
                  numeric severity re-labelled the row on the client. */}
              {typeof a.severity_label === "string" && a.severity_label ? (
                <span className={`chip${a.severity === 2 ? " state" : ""}`}>{a.severity_label}</span>
              ) : (
                <StateWord
                  state="CANNOT_CHECK"
                  reason={{ template: "CANNOT_CHECK_NO_SEVERITY_LABEL", params: {} }}
                />
              )}
            </td>
            <td>{String(a.device ?? "—")}</td>
            <td>{String(a.event_type ?? "—")}</td>
            <td className="code">{String(a.code ?? "—")}</td>
            <td className="num">{Number(a.count ?? 1)}</td>
            <td>{a.last_seen ? formatDate(String(a.last_seen)) : "—"}</td>
            <td>{a.active ? "Yes" : "No"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Findings tab
   ════════════════════════════════════════════════════════════════════════════ */

function FindingsTab({ findings, canRaiseWo, countFigure }: {
  findings: InterventionSummaryItem[];
  canRaiseWo?: boolean;
  countFigure?: Figure;
}): ReactNode {
  if (findings.length === 0) {
    // An empty list is NOT a verified-quiet block: no findings computation runs
    // for a block, so "No open findings" asserted a check that never happened.
    // The panel's withheld count carries the real reason.
    return (
      <EmptyState
        variant="no_data"
        title="Findings are not computed for a block"
        message={
          reasonSentence(countFigure)
          ?? "No findings store is wired to the block panel, so the count is withheld rather than shown as zero."
        }
      />
    );
  }
  return (
    <table className="table" aria-label="Findings">
      <thead>
        <tr>
          <th>Class</th>
          <th>Lane</th>
          <th>Equipment</th>
          <th><span className="sr-only">Actions</span></th>
        </tr>
      </thead>
      <tbody>
        {findings.map((f, i) => (
          <tr key={f.id ?? i}>
            <td><span className="chip state">{f.class ?? "finding"}</span></td>
            <td>{f.lane ?? "—"}</td>
            <td className="muted">{f.equipment_id ?? "—"}</td>
            <td>
              {canRaiseWo ? (
                <button type="button" className="btn small" disabled>Raise work order</button>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Helpers
   ════════════════════════════════════════════════════════════════════════════ */

function formatAge(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
  return `${Math.floor(seconds / 3600)} h`;
}

function formatDate(iso?: string): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
